/*
 * "A new version is available · Reload": an open tab keeps running the build it loaded (old
 * chunks stay served), so after a deploy it can show yesterday's app until reloaded. Every
 * 2 minutes, and when the tab regains focus, compare the deployed platform build (the version
 * in index.html's asset paths) and the tenant's extension pointer with what this tab runs.
 */
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/kit'
import { APP_BASE } from '../base'

const CHECK_EVERY_MS = 120_000

/** The platform build this tab runs: "/platform/<version>/" in production, null in dev. */
export function platformVersion(baseUrl: string = import.meta.env.BASE_URL): string | null {
    return /\/platform\/([^/]+)\//.exec(baseUrl)?.[1] ?? null
}

async function deployedPlatform(): Promise<string | null> {
    const html = await fetch(APP_BASE, { cache: 'no-store', credentials: 'same-origin' }).then((r) => (r.ok ? r.text() : ''))
    return /\/platform\/([^/"]+)\/assets\//.exec(html)?.[1] ?? null
}

async function deployedExtension(slug: string): Promise<string | null> {
    const response = await fetch(`/ext/${slug}/current.json`, { cache: 'no-store', credentials: 'same-origin' })
    if (!response.ok) return null
    return ((await response.json()) as { version?: string }).version ?? null
}

export function UpdateNotice({ slug, extensionVersion }: { slug: string; extensionVersion: string | null }) {
    const { t } = useTranslation()
    const [stale, setStale] = useState(false)
    useEffect(() => {
        const running = platformVersion()
        if (!running && !extensionVersion) return // dev server: nothing to compare
        let cancelled = false
        const check = async () => {
            try {
                const [platform, extension] = await Promise.all([running ? deployedPlatform() : null, extensionVersion ? deployedExtension(slug) : null])
                if (cancelled) return
                if ((running && platform && platform !== running) || (extensionVersion && extension && extension !== extensionVersion)) setStale(true)
            } catch {
                // Offline or signed out: try again next time.
            }
        }
        const timer = window.setInterval(() => void check(), CHECK_EVERY_MS)
        window.addEventListener('focus', check)
        return () => {
            cancelled = true
            window.clearInterval(timer)
            window.removeEventListener('focus', check)
        }
    }, [slug, extensionVersion])
    if (!stale) return null
    return (
        <div
            role="status"
            data-testid="update-notice"
            className="fixed right-[16px] bottom-[16px] z-50 flex items-center gap-[12px] rounded-[12px] bg-[var(--surface-raised)] py-[10px] pr-[10px] pl-[16px] text-[14px] leading-[20px] font-normal text-[var(--text-default)] shadow-[var(--shadow-popout)]"
        >
            {t('app.updateAvailable')}
            <Button variant="primary" onClick={() => window.location.reload()}>{t('app.reload')}</Button>
        </div>
    )
}
